// Guest App Internationalization (i18n) module
// Re-exports all i18n utilities for convenient importing

export {
  translations,
  supportedLanguages,
  DEFAULT_LANGUAGE,
  getLocalTranslation,
  getLanguageDirection,
  type TranslationKey,
  type Translations,
} from './translations';

/**
 * Check if a string looks like a translation key (dot-separated format)
 * e.g., "hotel.metro_herzliya.name" or "menu.hot_drinks.title"
 */
export function isTranslationKey(value: string): boolean {
  return /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_.]*$/i.test(value);
}

/**
 * Format a translation key into a readable display name
 * e.g., "hotel.metro_herzliya.name" -> "Metro Herzliya"
 * e.g., "menu.hot_drinks.title" -> "Hot Drinks"
 */
export function formatKeyAsDisplayName(key: string): string {
  // Extract the meaningful part (usually the middle segment)
  const parts = key.split('.');
  
  // Get the most meaningful segment (skip first and last if more than 2 parts)
  let meaningfulPart = parts.length > 2 ? parts[1] : parts[parts.length - 1];
  
  // Replace underscores with spaces and capitalize each word
  return meaningfulPart
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Resolve a value that might be a translation key
 * Uses the translate function, and falls back to formatted key if needed
 */
export function resolveDisplayValue(
  value: string | undefined | null,
  translateFn: (key: string) => string
): string {
  if (!value) return '';
  
  // If it looks like a translation key, try to translate it
  if (isTranslationKey(value)) {
    const translated = translateFn(value);
    // If translation returns the key itself, format it nicely
    if (translated === value) {
      return formatKeyAsDisplayName(value);
    }
    return translated;
  }
  
  // Not a translation key, return as-is
  return value;
}
